'use client';

/**
 * LUCAT CONNECT — Écran de premier choix du mode d'accueil * Affiché uniquement lorsqu'aucun mode n'a encore été sélectionné.
 */

import React from 'react';
import { useHomeMode, type HomeMode } from '@/lib/HomeModeContext';
import AppLogo from '@/components/ui/AppLogo';
import EmergencyButton from '@/components/EmergencyButton';

export default function HomeModeSelectionScreen() {
  const { setHomeMode } = useHomeMode();

  const handleChoose = (mode: HomeMode) => {
    setHomeMode(mode);
  };

  return (
    <div style={{
      minHeight: '100dvh',
      display: 'flex',
      flexDirection: 'column',
      background: 'linear-gradient(160deg, #EFF6FF 0%, #FBF8F3 60%, #F0FBE8 100%)',
      overflow: 'hidden',
    }}>
      {/* Header minimal avec logo + urgence */}
      <header style={{
        background: 'linear-gradient(135deg, #1A2744 0%, #0F2040 100%)',
        padding: '0 20px',
        height: '72px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexShrink: 0,
        paddingLeft: 'max(20px, env(safe-area-inset-left))',
        paddingRight: 'max(20px, env(safe-area-inset-right))',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <AppLogo size={40} />
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ color: '#5DBB1F', fontWeight: 800, fontSize: '20px', letterSpacing: '0.04em' }}>LUCAT</span>
            <span style={{ color: '#C0C8D4', fontWeight: 600, fontSize: '20px', letterSpacing: '0.04em' }}>CONNECT</span>
          </div>
        </div>
        <EmergencyButton variant="header" />
      </header>

      {/* Main content */}
      <main style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px 32px',
        gap: '28px',
        minHeight: 0,
        overflow: 'hidden',
      }}>
        {/* Title block */}
        <div style={{ textAlign: 'center', flexShrink: 0 }}>
          <h1 style={{
            fontSize: 'clamp(28px, 3.5vw, 40px)',
            fontWeight: 900,
            color: '#1A2744',
            margin: '0 0 10px',
            lineHeight: 1.15,
          }}>
            Choisissez votre accueil
          </h1>
          <p style={{
            fontSize: 'clamp(18px, 2vw, 22px)',
            color: '#5A6A7A',
            fontWeight: 500,
            margin: 0,
          }}>
            Vous pourrez modifier ce choix plus tard dans les réglages.
          </p>
        </div>

        {/* Cards row */}
        <div style={{
          display: 'flex',
          gap: '28px',
          width: '100%',
          maxWidth: '1100px',
          flex: 1,
          minHeight: 0,
        }}>
          {/* Card 1 — Accueil essentiel */}
          <button
            onClick={() => handleChoose('essential')}
            aria-label="Choisir l'accueil essentiel"
            style={{
              flex: 1,
              background: '#FFFFFF',
              border: '3px solid #5DBB1F',
              borderRadius: '24px',
              padding: '32px 28px',
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '20px',
              textAlign: 'center',
              boxShadow: '0 6px 32px rgba(93,187,31,0.15)',
              transition: 'all 0.2s ease',
              minHeight: 0,
              overflow: 'hidden',
            }}
          >
            {/* Visual preview — 3 contacts + 3 services */}
            <div style={{ flexShrink: 0 }}>
              {/* Row 1: contacts */}
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginBottom: '8px' }}>
                {[
                  { label: 'Marie', color: '#5DBB1F' },
                  { label: 'Jean', color: '#3B82F6' },
                  { label: 'Sophie', color: '#8B5CF6' },
                ].map(c => (
                  <div key={c.label} style={{
                    width: '56px', height: '56px', borderRadius: '50%',
                    background: c.color + '20',
                    border: `2.5px solid ${c.color}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '11px', fontWeight: 700, color: c.color,
                  }}>
                    {c.label[0]}
                  </div>
                ))}
              </div>
              {/* Row 2: services */}
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                {[
                  { label: 'Radio', color: '#F5A623', bg: '#FFF8EC' },
                  { label: 'Nouvelles', color: '#247B68', bg: '#E8F4F1' },
                  { label: 'Journée', color: '#3B82F6', bg: '#EFF6FF' },
                ].map(s => (
                  <div key={s.label} style={{
                    width: '56px', height: '56px', borderRadius: '12px',
                    background: s.bg,
                    border: `2px solid ${s.color}40`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '10px', fontWeight: 700, color: s.color,
                  }}>
                    {s.label}
                  </div>
                ))}
              </div>
            </div>

            <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: '10px', justifyContent: 'center' }}>
              <h2 style={{
                fontSize: 'clamp(24px, 2.8vw, 32px)',
                fontWeight: 900,
                color: '#1A2744',
                margin: 0,
                lineHeight: 1.15,
              }}>
                Accueil essentiel
              </h2>
              <p style={{
                fontSize: 'clamp(16px, 1.8vw, 20px)',
                color: '#5A6A7A',
                fontWeight: 500,
                margin: 0,
                lineHeight: 1.4,
              }}>
                Six grandes actions pour appeler vos proches et utiliser les services principaux.
              </p>
            </div>

            <div style={{
              width: '100%',
              background: 'linear-gradient(135deg, #5DBB1F 0%, #3D9A10 100%)',
              borderRadius: '16px',
              padding: '0 20px',
              minHeight: '72px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
              flexShrink: 0,
              boxShadow: '0 4px 16px rgba(93,187,31,0.35)',
            }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6" />
              </svg>
              <span style={{ fontSize: 'clamp(18px, 2vw, 22px)', fontWeight: 800, color: 'white', whiteSpace: 'nowrap' }}>
                Choisir l'accueil essentiel
              </span>
            </div>
          </button>

          {/* Card 2 — Accueil complet */}
          <button
            onClick={() => handleChoose('complete')}
            aria-label="Choisir l'accueil complet"
            style={{
              flex: 1,
              background: '#FFFFFF',
              border: '3px solid #1A2744',
              borderRadius: '24px',
              padding: '32px 28px',
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '20px',
              textAlign: 'center',
              boxShadow: '0 6px 32px rgba(26,39,68,0.10)',
              transition: 'all 0.2s ease',
              minHeight: 0,
              overflow: 'hidden',
            }}
          >
            {/* Visual preview — compact layout representation */}
            <div style={{ flexShrink: 0, width: '100%', maxWidth: '240px' }}>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                {/* Left column preview */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <div style={{ height: '28px', background: '#5DBB1F20', border: '1.5px solid #5DBB1F40', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span style={{ fontSize: '9px', fontWeight: 700, color: '#5DBB1F' }}>Parler à LUCAT</span>
                  </div>
                  {['Marie', 'Jean', 'Sophie'].map(n => (
                    <div key={n} style={{ height: '22px', background: '#F8F9FA', border: '1px solid #E5E7EB', borderRadius: '6px', display: 'flex', alignItems: 'center', paddingLeft: '6px' }}>
                      <span style={{ fontSize: '9px', fontWeight: 600, color: '#1A2744' }}>{n}</span>
                    </div>
                  ))}
                </div>
                {/* Right column preview */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {[
                    { label: 'Nouvelles', color: '#247B68', bg: '#E8F4F1' },
                    { label: 'Radio', color: '#F5A623', bg: '#FFF8EC' },
                    { label: 'Agenda', color: '#3B82F6', bg: '#EFF6FF' },
                    { label: 'Quiz', color: '#8B5CF6', bg: '#F5F3FF' },
                  ].map(s => (
                    <div key={s.label} style={{ height: '22px', background: s.bg, border: `1px solid ${s.color}40`, borderRadius: '6px', display: 'flex', alignItems: 'center', paddingLeft: '6px' }}>
                      <span style={{ fontSize: '9px', fontWeight: 600, color: s.color }}>{s.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: '10px', justifyContent: 'center' }}>
              <h2 style={{
                fontSize: 'clamp(24px, 2.8vw, 32px)',
                fontWeight: 900,
                color: '#1A2744',
                margin: 0,
                lineHeight: 1.15,
              }}>
                Accueil complet
              </h2>
              <p style={{
                fontSize: 'clamp(16px, 1.8vw, 20px)',
                color: '#5A6A7A',
                fontWeight: 500,
                margin: 0,
                lineHeight: 1.4,
              }}>
                Toutes vos informations, vos nouvelles et vos raccourcis sur un même écran.
              </p>
            </div>

            <div style={{
              width: '100%',
              background: 'linear-gradient(135deg, #1A2744 0%, #0F1B2D 100%)',
              borderRadius: '16px',
              padding: '0 20px',
              minHeight: '72px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
              flexShrink: 0,
              boxShadow: '0 4px 16px rgba(26,39,68,0.25)',
            }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6" />
              </svg>
              <span style={{ fontSize: 'clamp(18px, 2vw, 22px)', fontWeight: 800, color: 'white', whiteSpace: 'nowrap' }}>
                Choisir l'accueil complet
              </span>
            </div>
          </button>
        </div>
      </main>
    </div>
  );
}
